import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Link,
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import type {
  ActivitySubjectDetail,
  ActivitySubjectRecommendation,
  ActivitySubjectThreadItem,
} from '@shared/types/activity-feed';
import type {
  DashboardChatPageAction,
  DashboardChatPageItem,
} from '@shared/types/dashboard-chat';
import type { ThreadTopicSummary } from '@shared/types/thread-work';
import { WorkActionCard } from '@/components/work/WorkActionCard';
import { getSubjectWorkActions } from '@/lib/work-actions';
import { Button } from '@/components/ui/Button';
import { ThreadLivingCard } from '@/components/activity/ThreadLivingCard';
import { AccountTaskList } from '@/components/activity/AccountTaskList';
import { useLivingWorkGestures } from '@/components/activity/useLivingWorkGestures';
import type { AccountsOutlet } from '@/pages/AccountsLayout';
import {
  ThreadTimeline,
} from '@/components/activity/ThreadTimeline';
import { AccountChain } from '@/components/activity/AccountChain';
import {
  cancelActivityGmailWait,
  cancelActivityExactProviderWait,
  cancelActivityTopicWait,
  confirmActivityOutcome,
  dismissActivityRecommendation,
  getActivitySubjectDetail,
  monitorActivityGmailReply,
  monitorActivityExactProvider,
  snoozeActivityTopic,
} from '@/lib/activity-feed';
import {
  useAppAssistant,
  useRegisterAppAssistantContext,
} from '@/lib/app-assistant-context';
import { useThreadStateRealtime } from '@/components/activity/useThreadStateRealtime';
import { useProposalExpiryRefresh } from '@/components/activity/useProposalExpiryRefresh';
import { buildSubjectThreadPath, resolveSubjectThreadRoute } from '@/lib/thread-routes';
import { accountListFilters } from '@/lib/account-directory';
import { buildThreadDecisionFocus } from '@/lib/thread-decision-focus';
import { focusThreadTopic } from '@/lib/thread-topic-focus';
import { ACCOUNT_TASK_COMPLETED_EVENT } from '@/components/activity/AccountCapabilityLauncher';
import { CrmAccountFacts } from '@/components/accounts/CrmAccountFacts';
import { AccountKnowledge } from '@/components/accounts/AccountKnowledge';
import { AccountHeader } from '@/components/accounts/AccountHeader';
import { AttachAccountControl } from '@/components/accounts/AttachAccountControl';

/** Apps that start from an account (prebuilt tools that take it as context). */
const ACCOUNT_TOOLS = [
  { key: 'qualify_account', label: 'Qualifier un compte' },
  { key: 'prepare_meeting', label: 'Préparer le rendez-vous' },
  { key: 'analyze_call', label: 'Analyser un appel' },
  { key: 'draft_followup', label: 'Préparer le suivi' },
] as const;
import { useCrmAccount } from '@/lib/use-crm-account';

const CANDIDATE_RECOMMENDATION_STATES = new Set<ActivitySubjectRecommendation['state']>([
  'open',
  'weak_covered',
]);

const buildRecommendationPageAction = (
  recommendation: ActivitySubjectRecommendation,
  id: string
): DashboardChatPageAction | null => {
  if (!recommendation.action) return null;
  const action = recommendation.action;

  return {
    id,
    kind: 'view_action',
    label: recommendation.label,
    description: recommendation.summary,
    action,
    actionLabel: action.label,
  };
};

const buildRecommendationPageItem = (
  recommendation: ActivitySubjectRecommendation,
  id: string,
  primaryActionId: string | null
): DashboardChatPageItem => ({
  id,
  label: recommendation.label,
  summary: recommendation.summary,
  meta: recommendation.stateLabel,
  priority: recommendation.priority,
  primaryActionId,
});

const buildThreadItemPageItem = (
  item: ActivitySubjectThreadItem,
  id: string
): DashboardChatPageItem => ({
  id,
  label: item.title,
  summary: item.summary,
  meta: item.toolLabel ?? item.kind,
  priority: item.tone,
});

export const ThreadPage = () => {
  const { subjectId } = useParams<{ subjectId: string }>();
  const [params] = useSearchParams();
  // A new account owns a fresh page instance, including async loads and subscriptions.
  // Filters/topic/result stay within the same instance; legacy identity hints do not.
  const identity = JSON.stringify([subjectId, params.get('key'), params.get('kind'), params.get('label')]);
  return <ThreadAccountPage key={identity} />;
};

const ThreadAccountPage = () => {
  const { subjectId } = useParams<{ subjectId: string }>();
  const [searchParams] = useSearchParams();
  const routeKey = searchParams.get('key');
  const routeKind = searchParams.get('kind');
  const routeLabel = searchParams.get('label');
  const location = useLocation();
  const navigate = useNavigate();
  const { pinDecisionFocus, submitAssistantCommand, openWork } = useAppAssistant();
  const routeState = useMemo(
    () => resolveSubjectThreadRoute(subjectId, new URLSearchParams([
      ...(routeKey ? [['key', routeKey]] : []),
      ...(routeKind ? [['kind', routeKind]] : []),
      ...(routeLabel ? [['label', routeLabel]] : []),
    ])),
    [routeKey, routeKind, routeLabel, subjectId]
  );
  const [detail, setDetail] = useState<ActivitySubjectDetail | null>(null);
  const loadSequence = useRef(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [taskError, setTaskError] = useState<string | null>(null);
  const [threadLivingCardEnabled, setThreadLivingCardEnabled] = useState(false);
  const [threadWaitTimeEnabled, setThreadWaitTimeEnabled] = useState(false);
  const [threadWaitGmailEnabled, setThreadWaitGmailEnabled] = useState(false);
  const [threadWaitCalendarEnabled, setThreadWaitCalendarEnabled] = useState(false);
  const [threadWaitHubSpotEnabled, setThreadWaitHubSpotEnabled] = useState(false);
  const [threadChatDecisionFocusEnabled, setThreadChatDecisionFocusEnabled] =
    useState(false);
  const [dismissingRecommendationId, setDismissingRecommendationId] = useState<
    string | null
  >(null);
  const requestedTopicId = searchParams.get('topic')?.trim() || null;
  const requestedResultId = searchParams.get('result')?.trim() || null;
  const requestedFlowRunId = requestedResultId ? null : searchParams.get('flow')?.trim() || null;
  const requestedAccountSearch = searchParams.get('q')?.trim() || null;
  const accountFilters = accountListFilters(searchParams);
  const versionParam = searchParams.get('version');
  const requestedVersion = (requestedResultId || requestedFlowRunId) && versionParam && /^\d{1,10}$/.test(versionParam)
    && Number(versionParam) <= 2147483647 ? String(Number(versionParam)) : null;
  const topicFocus = useMemo(
    () =>
      detail?.threadState ? focusThreadTopic(detail.threadState, requestedTopicId) : null,
    [detail?.threadState, requestedTopicId]
  );
  const displayedThreadState = topicFocus?.threadState ?? detail?.threadState ?? null;

  const loadThread = useCallback(
    async (options?: { background?: boolean }) => {
      const sequence = ++loadSequence.current;
      if (!routeState) {
        setLoading(false);
        setError('Ce thread ne peut pas être ouvert sans sujet métier.');
        return;
      }

      if (!options?.background) {
        setLoading(true);
        setError(null);
      }
      try {
        const response = await getActivitySubjectDetail({
          subjectId: routeState.subjectId,
          subjectKey: routeState.subjectKey,
          subjectKind: routeState.subjectKind,
          subjectLabel: routeState.subjectLabel,
        });
        if (sequence !== loadSequence.current) return;
        setDetail(response.detail);
        setThreadLivingCardEnabled(response.threadLivingCardEnabled === true);
        setThreadWaitTimeEnabled(response.threadWaitTimeEnabled === true);
        setThreadWaitGmailEnabled(response.threadWaitGmailEnabled === true);
        setThreadWaitCalendarEnabled(response.threadWaitCalendarEnabled === true);
        setThreadWaitHubSpotEnabled(response.threadWaitHubSpotEnabled === true);
        setThreadChatDecisionFocusEnabled(
          response.threadChatDecisionFocusEnabled === true
        );
      } catch (loadError) {
        if (sequence !== loadSequence.current) return;
        const message =
          loadError instanceof Error
            ? loadError.message
            : 'Impossible de charger le Thread.';

        if (options?.background) {
          console.warn('Thread background refresh failed', message);
        } else {
          setError('Impossible de charger ce compte. Réessayez dans un instant.');
        }
      } finally {
        if (sequence === loadSequence.current) setLoading(false);
      }
    },
    [routeState]
  );
  const refreshThread = useCallback(() => {
    void loadThread({ background: true });
  }, [loadThread]);

  useThreadStateRealtime({
    enabled: threadLivingCardEnabled && Boolean(detail?.threadState?.threadId),
    threadId: detail?.threadState?.threadId ?? null,
    onRefresh: refreshThread,
  });
  useProposalExpiryRefresh({
    threadStates: [displayedThreadState],
    onRefresh: refreshThread,
  });

  useEffect(() => {
    void loadThread();
    return () => { loadSequence.current += 1; };
  }, [loadThread]);

  const crm = useCrmAccount(detail?.subjectId ?? null, refreshThread);

  useEffect(() => {
    const refresh = () => refreshThread();
    window.addEventListener(ACCOUNT_TASK_COMPLETED_EVENT, refresh);
    return () => window.removeEventListener(ACCOUNT_TASK_COMPLETED_EVENT, refresh);
  }, [refreshThread]);

  useEffect(() => {
    const refreshOnFocus = () => refreshThread();
    const refreshOnVisibility = () => {
      if (document.visibilityState === 'visible') refreshThread();
    };

    window.addEventListener('focus', refreshOnFocus);
    document.addEventListener('visibilitychange', refreshOnVisibility);
    return () => {
      window.removeEventListener('focus', refreshOnFocus);
      document.removeEventListener('visibilitychange', refreshOnVisibility);
    };
  }, [refreshThread]);

  useEffect(() => {
    if (!detail) return;
    const canonicalPath = buildSubjectThreadPath(detail);
    const canonicalParams = new URLSearchParams();
    if (topicFocus?.focused && requestedTopicId) {
      canonicalParams.set('topic', requestedTopicId);
    }
    if (requestedResultId) canonicalParams.set('result', requestedResultId);
    if (requestedFlowRunId) canonicalParams.set('flow', requestedFlowRunId);
    if (requestedVersion) canonicalParams.set('version', requestedVersion);
    if (requestedFlowRunId) for (const key of ['outputTab', 'outputSection']) {
      const value = new URLSearchParams(location.search).get(key);
      if (value !== null && value.length <= 255) canonicalParams.set(key, value);
    }
    for (const [key, value] of new URLSearchParams(accountFilters)) canonicalParams.set(key, value);
    const canonicalSearch = canonicalParams.size > 0
      ? `?${canonicalParams.toString()}`
      : '';
    if (location.pathname === canonicalPath && location.search === canonicalSearch)
      return;
    navigate(`${canonicalPath}${canonicalSearch}`, { replace: true });
  }, [
    detail,
    location.pathname,
    location.search,
    navigate,
    requestedTopicId,
    requestedResultId,
    requestedFlowRunId,
    requestedVersion,
    requestedAccountSearch,
    accountFilters,
    topicFocus?.focused,
  ]);

  const focusedWork = useMemo(
    () => detail && requestedTopicId
      ? getSubjectWorkActions(detail).find((work) => work.id === requestedTopicId) ?? null
      : null,
    [detail, requestedTopicId]
  );

  const candidateRecommendations = useMemo(() => {
    if (!detail) return [];
    return detail.recommendations.filter((recommendation) =>
      CANDIDATE_RECOMMENDATION_STATES.has(recommendation.state)
    );
  }, [detail]);
  const assistantRecommendations = useMemo(() => {
    const currentTopic = displayedThreadState?.currentTopic;
    if (!threadLivingCardEnabled || !currentTopic) {
      return candidateRecommendations;
    }
    return candidateRecommendations.filter(
      (recommendation) =>
        currentTopic.sourceActionId !== null &&
        recommendation.sourceActionId === currentTopic.sourceActionId &&
        (currentTopic.sourceRunId === null ||
          recommendation.sourceRunId === currentTopic.sourceRunId)
    );
  }, [candidateRecommendations, displayedThreadState, threadLivingCardEnabled]);

  const assistantContext = useMemo(() => {
    if (!detail) return null;

    const availableActions: DashboardChatPageAction[] = [];
    const visibleItems: DashboardChatPageItem[] = [
      {
        id: 'thread.subject',
        label: detail.subjectLabel,
        summary: detail.headline,
        meta: detail.subjectSubtitle ?? detail.subjectKind,
        priority: detail.severity,
      },
    ];

    if (!threadLivingCardEnabled && detail.nextStepAction) {
      const nextStepAction = detail.nextStepAction;
      availableActions.push({
        id: 'thread.next_step',
        kind: 'view_action',
        label: detail.nextStepLabel ?? nextStepAction.label,
        description: detail.nextStepDescription,
        action: nextStepAction,
        actionLabel: nextStepAction.label,
      });
    }

    assistantRecommendations.slice(0, 8).forEach((recommendation, index) => {
      const actionId = `thread.recommendation.candidate.${index}`;
      const action = buildRecommendationPageAction(recommendation, actionId);
      if (action) availableActions.push(action);
      visibleItems.push(
        buildRecommendationPageItem(
          recommendation,
          `thread.visible.open.${index}`,
          action ? actionId : null
        )
      );
    });

    detail.thread.slice(0, 6).forEach((item, index) => {
      visibleItems.push(buildThreadItemPageItem(item, `thread.timeline.${index}`));
    });

    const candidateLabels = assistantRecommendations
      .slice(0, 5)
      .map((recommendation) => recommendation.label);
    const timelineLabels = detail.thread.slice(0, 4).map((item) => item.title);

    return {
      surface: 'home' as const,
      routePath: `${location.pathname}${location.search}`,
      routeLabel: detail.subjectLabel,
      visibleSummary: [
        `Sujet : ${detail.subjectLabel}.`,
        detail.headline ? `État : ${detail.headline}` : null,
        displayedThreadState?.currentTopic
          ? `${topicFocus?.focused ? 'Analyse ciblée' : 'Priorité actuelle'} : ${displayedThreadState.currentTopic.title}. État : ${displayedThreadState.currentTopic.visibleState}. Thread ${
              displayedThreadState.threadId ?? 'legacy'
            }, topic ${displayedThreadState.currentTopic.id}, décision ${
              displayedThreadState.currentTopic.decision?.id ?? 'absente'
            }, version ${displayedThreadState.currentTopic.version}.`
          : null,
        detail.nextStepLabel ? `Prochaine tâche : ${detail.nextStepLabel}` : null,
        candidateLabels.length > 0
          ? `Actions candidates : ${candidateLabels.join('; ')}.`
          : null,
        timelineLabels.length > 0 ? `Enchaînement : ${timelineLabels.join('; ')}.` : null,
      ]
        .filter(Boolean)
        .join(' '),
      visibleEntities: [detail.subjectLabel, detail.subjectSubtitle].filter(
        (value): value is string => Boolean(value)
      ),
      visibleItems: visibleItems.slice(0, 20),
      availableActions: availableActions.slice(0, 12),
    };
  }, [
    assistantRecommendations,
    detail,
    displayedThreadState,
    location.pathname,
    location.search,
    threadLivingCardEnabled,
    topicFocus?.focused,
  ]);
  useRegisterAppAssistantContext(assistantContext);

  const handleDismissRecommendation = async (
    recommendation: ActivitySubjectRecommendation,
    triageReason: 'already_done' | 'not_relevant' | 'later' = 'not_relevant'
  ) => {
    if (!detail) return;

    setDismissingRecommendationId(recommendation.id);
    try {
      await dismissActivityRecommendation({
        subjectId: detail.subjectId,
        subjectKey: detail.subjectKey,
        subjectKind: detail.subjectKind,
        subjectLabel: detail.subjectLabel,
        recommendationId: recommendation.id,
        recommendationLabel: recommendation.label,
        triageReason,
        sourceRunId: recommendation.sourceRunId,
        sourceActionId: recommendation.sourceActionId,
        sourceActionIndex: recommendation.sourceActionIndex,
      });
      await loadThread({ background: true });
    } catch (dismissError) {
      setError(
        dismissError instanceof Error
          ? dismissError.message
          : 'Impossible de mettre à jour la suite.'
      );
    } finally {
      setDismissingRecommendationId(null);
    }
  };

  const handleSnoozeTopic = async ({
    topic,
    wakeAt,
    timezone,
    reason,
  }: {
    topic: ThreadTopicSummary;
    wakeAt: string;
    timezone: string;
    reason: string | null;
  }) => {
    await snoozeActivityTopic({
      topicId: topic.id,
      expectedTopicVersion: topic.version,
      wakeAt,
      timezone,
      reason,
      idempotencyKey: `wait:snooze:${crypto.randomUUID()}`,
    });
    await loadThread({ background: true });
  };

  const handleCancelWait = async ({
    topic,
    waitId,
  }: {
    topic: ThreadTopicSummary;
    waitId: string;
  }) => {
    const cancel =
      topic.activeWait?.kind !== 'activity_event'
        ? cancelActivityTopicWait
        : topic.activeWait.providerKind === 'gmail' ||
            topic.activeWait.providerKind === null
          ? cancelActivityGmailWait
          : cancelActivityExactProviderWait;
    await cancel({
      topicId: topic.id,
      waitId,
      expectedTopicVersion: topic.version,
      idempotencyKey: `wait:cancel:${crypto.randomUUID()}`,
    });
    await loadThread({ background: true });
  };

  const handleConfirmOutcome = async ({
    topic,
    evidence,
  }: { topic: ThreadTopicSummary; evidence: string }) => {
    await confirmActivityOutcome({
      topicId: topic.id,
      expectedTopicVersion: topic.version,
      evidence,
      idempotencyKey: `outcome:confirm:${crypto.randomUUID()}`,
    });
    await loadThread({ background: true });
  };

  const handleMonitorGmailReply = async ({
    topic,
    gmailThreadRefId,
    expiresAt,
    timezone,
    reason,
  }: {
    topic: ThreadTopicSummary;
    gmailThreadRefId: string;
    expiresAt: string;
    timezone: string;
    reason: string | null;
  }) => {
    await monitorActivityGmailReply({
      topicId: topic.id,
      gmailThreadRefId,
      expectedTopicVersion: topic.version,
      expiresAt,
      timezone,
      reason,
      idempotencyKey: `wait:gmail:${crypto.randomUUID()}`,
    });
    await loadThread({ background: true });
  };

  const handleMonitorExactProvider = async ({
    topic,
    provider,
    providerRefId,
    expiresAt,
    timezone,
    reason,
  }: {
    topic: ThreadTopicSummary;
    provider: 'google_calendar' | 'hubspot';
    providerRefId: string;
    expiresAt: string;
    timezone: string;
    reason: string | null;
  }) => {
    await monitorActivityExactProvider({
      topicId: topic.id,
      provider,
      providerRefId,
      expectedTopicVersion: topic.version,
      expiresAt,
      timezone,
      reason,
      idempotencyKey: `wait:${provider}:${crypto.randomUUID()}`,
    });
    await loadThread({ background: true });
  };

  const startFocusedThreadInteraction = (
    topic: ThreadTopicSummary,
    kind: 'why' | 'challenge' | 'alternative' | 'wait'
  ) => {
    if (!detail?.threadState || !threadChatDecisionFocusEnabled) {
      if (kind === 'alternative') {
        submitAssistantCommand(
          `Propose-moi une autre suite pour « ${topic.title} ». Explique les compromis et ne remplace pas la décision actuelle sans ma confirmation.`
        );
      }
      return;
    }
    const focus = buildThreadDecisionFocus({
      detail,
      threadState: displayedThreadState ?? detail.threadState,
      topic,
    });
    if (!focus) {
      setError('Cette décision n’est plus disponible. Actualisez le Thread.');
      return;
    }
    const message =
      kind === 'why'
        ? 'Pourquoi cette prochaine étape ?'
        : kind === 'challenge'
          ? `Challenge la priorité « ${topic.title} » à partir des faits enregistrés. Montre ce qui pourrait la rendre prématurée ou moins utile, sans modifier la décision officielle.`
          : kind === 'wait'
            ? `Que se passe-t-il si j’attends pour « ${topic.title} » ? Propose uniquement une attente réellement vérifiable et ne change rien sans ma confirmation.`
            : `Propose-moi une autre suite pour « ${topic.title} ». Compare les compromis avec la décision actuelle et ne la remplace pas sans ma confirmation explicite.`;
    pinDecisionFocus(focus);
    submitAssistantCommand(message, {
      threadInteraction: {
        kind,
        focus,
        idempotencyKey: `thread-chat:${kind}:${crypto.randomUUID()}`,
        ...(kind === 'wait'
          ? {
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
            }
          : {}),
      },
    });
  };

  // One task, one row (26/10): the account's tasks with Mon travail's row and gestures.
  const layout = useOutletContext<AccountsOutlet | undefined>();
  const { handlers: gestures, dialogs: gestureDialogs } = useLivingWorkGestures({
    onChanged: async () => {
      await Promise.all([layout?.reloadSubjects(), loadThread({ background: true })]);
    },
    onError: setTaskError,
  });

  // Account header (26/10): the apps that start from an account open with it as context.
  const currentTopic = displayedThreadState?.currentTopic;
  const accountLaunchContext = detail ? {
    subjectId: detail.subjectId,
    accountSubjectId: detail.accountSubjectId,
    topicId: currentTopic?.decision ? currentTopic.id : null,
    expectedTopicVersion: currentTopic?.decision ? currentTopic.version : null,
    decisionId: currentTopic?.decision?.id ?? null,
    objectiveRef: currentTopic?.decision?.evidenceRefs.find((reference) => reference.startsWith('objective:')) ?? null,
  } : null;

  // « Ce qu'on sait » (26/10): the account's exact identity, never a contact or a name.
  const knowledgeAccountId = detail ? (detail.accountSubjectId ?? (detail.subjectKind === 'company' ? detail.subjectId : null)) : null;

  return (
    <div className="min-h-full bg-domino-ivory">
      <div className="border-b border-domino-border bg-domino-ivory px-5 py-3 sm:px-7 md:hidden xl:px-10">
        <Link
          to={`/accounts${accountFilters}`}
          className="inline-flex items-center gap-2 text-sm font-medium text-domino-inkSoft transition hover:text-domino-aubergine"
        >
          <ArrowLeft className="h-4 w-4" />
          Comptes
        </Link>
      </div>

      {loading || (routeState && detail && detail.subjectId !== routeState.subjectId) ? (
        <div className="flex min-h-[60vh] items-center justify-center">
          <div className="h-7 w-7 animate-spin rounded-full border-2 border-gray-300 border-t-gray-900" />
        </div>
      ) : error || !detail ? (
        <div className="mx-auto max-w-3xl px-6 py-12 xl:px-10">
          <div className="border-t border-red-200 py-4 text-sm text-red-700">
            {error ?? 'Compte introuvable.'}
            <Button className="mt-3" variant="secondary" onClick={() => void loadThread()}>Réessayer</Button>
          </div>
        </div>
      ) : (
        <main className="mx-auto max-w-4xl px-5 py-7 sm:px-7 xl:px-10">
          <AccountHeader
            title={detail.subjectLabel}
            back={(topicFocus?.focused || requestedResultId || requestedFlowRunId) ? (
              <Link
                to={`${buildSubjectThreadPath(detail)}${accountFilters}`}
                className="mb-3 inline-flex items-center gap-2 text-sm font-medium text-domino-aubergine hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-domino-prune"
              >
                <ArrowLeft className="h-4 w-4" />
                Vue du compte
              </Link>
            ) : null}
            account={crm.loaded ? crm.account : undefined}
            crmError={crm.error}
            addToCrmRunId={detail.thread.find((item) => item.sourceRunId && !item.taskActionRunId)?.sourceRunId ?? null}
            attach={detail.subjectKind === 'company'
              ? <AttachAccountControl subjectId={detail.subjectId} label={detail.subjectLabel} onAttached={() => { layout?.reload(); void layout?.reloadSubjects(); }} />
              : null}
            apps={[
              ...(crm.account ? [{ key: 'review', label: crm.reviewing ? 'Examen demandé…' : 'Examiner ce compte', onSelect: crm.requestReview }] : []),
              ...ACCOUNT_TOOLS.map(({ key, label }) => ({ key, label, onSelect: () => navigate(`/tools/${key}`, { state: { toolLaunchContext: accountLaunchContext } }) })),
              { key: 'chat', label: 'Autre demande', onSelect: () => openWork({ subjectId: detail.subjectId, subjectKey: detail.subjectKey, subjectKind: detail.subjectKind, subjectLabel: detail.subjectLabel, threadId: detail.threadState?.threadId ?? null, topicId: null, topicVersion: null, sourceRunId: null, sourceActionId: null, title: '', decisionFocus: null }) },
            ]}
          />

          {!requestedFlowRunId && !requestedResultId && (
            <AccountChain items={detail.thread} threadState={displayedThreadState} subjectId={detail.subjectId} />
          )}

          {!requestedFlowRunId && (threadLivingCardEnabled && displayedThreadState && !topicFocus?.focused && !requestedResultId ? (
            knowledgeAccountId && <>
              {taskError && <p role="alert" className="mb-3 text-sm text-domino-inkSoft">{taskError}</p>}
              <AccountTaskList subjects={layout?.subjects ?? []} accountSubjectId={knowledgeAccountId} {...gestures} />
            </>
          ) : threadLivingCardEnabled && displayedThreadState ? (
            <>
              <ThreadLivingCard
                detail={detail}
                threadState={displayedThreadState}
                sectionTitle={
                  requestedResultId
                    ? 'Travail repris'
                    : topicFocus?.focused
                      ? 'Analyse demandée'
                      : 'Prochaine action'
                }
                resultFocused={Boolean(requestedResultId)}
                triagingRecommendationId={dismissingRecommendationId}
                onTriageRecommendation={(recommendation, reason) => {
                  void handleDismissRecommendation(recommendation, reason);
                }}
                onAskAlternative={(topic) => {
                  startFocusedThreadInteraction(topic, 'alternative');
                }}
                onAskWhy={(topic) => startFocusedThreadInteraction(topic, 'why')}
                onChallenge={(topic) => startFocusedThreadInteraction(topic, 'challenge')}
                onAskWait={(topic) => startFocusedThreadInteraction(topic, 'wait')}
                threadChatDecisionFocusEnabled={threadChatDecisionFocusEnabled}
                threadWaitTimeEnabled={threadWaitTimeEnabled}
                threadWaitGmailEnabled={threadWaitGmailEnabled}
                threadWaitCalendarEnabled={threadWaitCalendarEnabled}
                threadWaitHubSpotEnabled={threadWaitHubSpotEnabled}
                onSnoozeTopic={handleSnoozeTopic}
                onMonitorGmailReply={handleMonitorGmailReply}
                onMonitorExactProvider={handleMonitorExactProvider}
                onCancelWait={handleCancelWait}
                onConfirmOutcome={handleConfirmOutcome}
              />
            </>
          ) : (
            <>
              {getSubjectWorkActions(detail).map(work => <section key={work.id} className="border-b border-domino-cardBorder py-5"><WorkActionCard work={work} /></section>)}
            </>
          ))}

          {!requestedFlowRunId && !requestedResultId && !topicFocus?.focused && knowledgeAccountId && (
            <AccountKnowledge accountSubjectId={knowledgeAccountId} contacts={crm.account?.status === 'synced' ? crm.account.contacts : []} />
          )}

          {crm.reviewError && (
            <p role="alert" className="border-b border-domino-cardBorder py-4 text-sm text-red-700">{crm.reviewError}</p>
          )}
          {!requestedFlowRunId && !requestedResultId && !topicFocus?.focused && (
            <CrmAccountFacts showCompany={false} showContacts={false} account={crm.account} error={crm.error} onRetry={crm.reload} onSync={crm.syncFromCrm} syncing={crm.syncing} />
          )}

          {/* The chain at the top is the account's history (26/10); the timeline stays only around a requested result. */}
          {(requestedFlowRunId || requestedResultId) && <ThreadTimeline
            key={detail.subjectId}
            items={detail.thread}
            subjectId={detail.subjectId}
            focusedTaskActionRunId={requestedResultId}
            focusedFlowRunId={requestedFlowRunId}
            subjectLabel={detail.subjectLabel}
            onContinueFocused={focusedWork
              ? () => openWork({ ...focusedWork.context, resultId: requestedResultId ?? undefined })
              : undefined}
          />}
        </main>
      )}
      {gestureDialogs}
    </div>
  );
};
